import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import EquipmentsRow from './EquipmentsRow';
import useStyles from './styles';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ equipments, t }) =>
  equipments.map(equip => <EquipmentsRow key={equip.id} t={t} {...equip} />);

const EquipmentsTable = ({
  className,
  equipments,
  count,
  page,
  handleChangePage,
  loading,
  ...rest
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const width = '11%';

  const theads = useMemo(
    () => () => (
      <TableRow
        sx={{
          '& .MuiTableCell-head': {
            whiteSpace: 'nowrap',
            textAlign: 'center'
          }
        }}
      >
        <TableCell variant="head" key="id" sx={{ width: "2%" }}>
          ID
        </TableCell>

        <TableCell variant="head" key="client" sx={{ width }}>
          {t('COMMON.CLIENT')}
        </TableCell>

        <TableCell variant="head" key="equipmentType" sx={{ width }}>
          {t('EQUIPMENT_VIEW.EQUIPMENT_TYPE')}
        </TableCell>

        <TableCell variant="head" key="distributor" sx={{ width }}>
          {t('EQUIPMENT_VIEW.DISTRIBUTOR')}
        </TableCell>

        <TableCell variant="head" key="brand" sx={{ width }}>
          {t('COMMON.BRAND')}
        </TableCell>

        <TableCell variant="head" key="model" sx={{ width }}>
          {t('COMMON.MODEL')}
        </TableCell>

        <TableCell variant="head" key="serialNumber" sx={{ width }}>
          {t('EQUIPMENT_VIEW.SERIAL_NUMBER')}
        </TableCell>

        <TableCell variant="head" key="year" sx={{ width }}>
          {t('COMMON.YEAR')}
        </TableCell>

        <TableCell
          variant="head"
          key="pending"
          align="center"
          sx={{ width }}
        >
          {t('COMMON.PENDING')}
        </TableCell>

        <TableCell variant="head" key="status" sx={{ width }}>
          {t('COMMON.STATUS')}
        </TableCell>
      </TableRow>
    ),
    [classes, t]
  );

  const tbody = useMemo(() => () => <Tbody t={t} equipments={equipments} />, [
    equipments,
    t
  ]);

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, equipments]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

EquipmentsTable.propTypes = {
  className: PropTypes.string,
  equipments: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool
};

EquipmentsTable.defaultProps = {
  count: 0
};

export default EquipmentsTable;
